/**
 * The family form's validation messages, in the reader's language (#2109 R2
 * lane L3; research §2.5 rule 5).
 *
 * Every message resolves through ONE four-tier lookup, the JSON Forms order:
 *
 *   1. field custom  — the field's own authored sentence for this failure
 *                      (`date_bound.message`, `date_compare.message`). It is
 *                      read off the LOCALISED version, so a current
 *                      translation of it is already in place.
 *   2. field keyword — a per-field override for this keyword. R2 definitions
 *                      carry none (the L0 entry validators refuse an `errors`
 *                      key), so no caller passes one yet; the tier exists so
 *                      the order is the research's and not a later retrofit.
 *   3. platform      — `validation.json` in the READER's language, when that
 *                      catalog has a non-empty value for the key. An empty
 *                      value is "untranslated" (R1 ruling R-A) and falls
 *                      through.
 *   4. English       — the sentence the platform has always shown. Callers
 *                      pass it from the lib that owns it (`fieldValueProblem`,
 *                      `rulesFromFields`), so an English reader sees exactly
 *                      what they saw before R2, word for word.
 *
 * Tier 3 never runs for an English reader: the English catalog is a template
 * for translators (it mirrors the lib sentences with placeholders), and the
 * lib sentence is the source of truth for English.
 *
 * Count-bearing messages (the "N questions need a look" summary) are plain
 * plural keys (`_one`/`_other` in English); i18next picks the CLDR category
 * through `Intl.PluralRules`, and `catalog-parity.test.ts` holds every target
 * catalog to its locale's categories.
 */
import type { i18n as I18n } from 'i18next';
import type { DefinitionField } from '@/lib/formDefinitions';
import { fieldValueProblem, isFieldValueEmpty, type FieldSpec } from '@/lib/intakeFields';
import type { DateRule } from '@/lib/dateRules';

export type ValidationKey =
  | 'field.required'
  | 'number.between'
  | 'number.atLeast'
  | 'number.atMost'
  | 'number.wholeBetween'
  | 'format.email'
  | 'format.phone'
  | 'format.zip'
  | 'date.invalid'
  | 'date.future'
  | 'date.past'
  | 'date.before'
  | 'date.after';

export interface ValidationRequest {
  key: ValidationKey;
  params?: Record<string, string | number>;
  /** Tier 1: the field's own authored sentence, already localised. */
  fieldCustom?: string | null;
  /** Tier 2: a per-field override for this keyword. */
  fieldKeyword?: string | null;
  /** Tier 4: the English the platform has always shown. When absent, the
   *  English catalog value is used. */
  english?: string | null;
}

type Resolver = Pick<I18n, 'getFixedT' | 'getResource'>;

const NS = 'validation';
const present = (s: unknown): s is string => typeof s === 'string' && s.trim() !== '';
/** A fixed `t` for the validation namespace. Loosened to a plain signature
 *  because the typed overloads cannot take a key picked at run time together
 *  with a parameter record. */
const fixedT = (i18n: Resolver, lng: string) =>
  i18n.getFixedT(lng, NS) as unknown as (key: string, params: Record<string, unknown>) => string;

/** The four-tier lookup. `lng` is the language the form renders in. */
export function resolveValidationMessage(i18n: Resolver, lng: string, req: ValidationRequest): string {
  if (present(req.fieldCustom)) return req.fieldCustom;
  if (present(req.fieldKeyword)) return req.fieldKeyword;
  if (lng !== 'en' && present(i18n.getResource(lng, NS, req.key))) {
    return fixedT(i18n, lng)(req.key, req.params ?? {});
  }
  if (present(req.english)) return req.english;
  return fixedT(i18n, 'en')(req.key, req.params ?? {});
}

/**
 * The keyword behind `fieldValueProblem(field, value)`, or null when the value
 * is fine. It mirrors that function branch for branch, and carries its English
 * as tier 4 — `validationMessages.test.ts` pins the two together, so a new
 * branch in the lib that this does not know about fails there, not on screen.
 */
export function fieldValueProblemRequest(field: FieldSpec, value: unknown): ValidationRequest | null {
  const english = fieldValueProblem(field, value);
  if (english === null) return null;
  if (!isFieldValueEmpty(field, value) && field.type === 'number' && typeof value === 'number') {
    const { min, max } = field;
    if (min != null && max != null && (value < min || value > max)) return { key: 'number.between', params: { min, max }, english };
    if (min != null && value < min) return { key: 'number.atLeast', params: { min }, english };
    if (max != null && value > max) return { key: 'number.atMost', params: { max }, english };
  }
  if (field.type === 'date' && typeof value === 'string') return { key: 'date.invalid', english };
  if (field.format) return { key: `format.${field.format}`, english };
  // A branch the mirror does not know: English, never a wrong keyword.
  return { key: 'field.required', fieldCustom: english };
}

/**
 * The date rules with each `problem` resolved for the reader. The rules are
 * built from the LOCALISED version (so a comparison names the other question
 * in the reader's language); this only swaps the sentences.
 *
 *  - A bound rule reads the field's `date_bound.message` as tier 1 — for a
 *    builder rule that is exactly the sentence `rulesFromFields` used.
 *  - A compare rule reads `date_compare.message` as tier 1. It is not a
 *    translatable slot in the L0 contract, so an authored comparison sentence
 *    stays in English; the platform default is translated.
 *  - The seeded #1111 rules carry pinned English and no field sentence, so
 *    they resolve through the platform catalog.
 */
export function localizeDateRules(
  rules: readonly DateRule[],
  fields: readonly DefinitionField[],
  resolve: (req: ValidationRequest) => string,
): DateRule[] {
  const byPath = new Map(fields.map((f) => [`${f.section_id}.${f.id}`, f]));
  const seeded = (rule: DateRule, field: DefinitionField | undefined) =>
    !field || (rule.bound ? !field.date_bound : !field.date_compare);
  return rules.map((rule) => {
    const field = byPath.get(`${rule.section}.${rule.key}`);
    const fromSeed = seeded(rule, field);
    if (rule.bound) {
      const problem = resolve({
        key: rule.bound === 'past' ? 'date.future' : 'date.past',
        fieldCustom: fromSeed ? null : field?.date_bound?.message,
        english: rule.problem,
      });
      return { ...rule, problem };
    }
    if (rule.compare) {
      const other = byPath.get(`${rule.compare.section}.${rule.compare.key}`);
      const problem = resolve({
        key: rule.compare.op === 'not_before' ? 'date.before' : 'date.after',
        params: { other: other?.label || rule.compare.key },
        fieldCustom: fromSeed ? null : field?.date_compare?.message,
        english: rule.problem,
      });
      return { ...rule, problem };
    }
    return rule;
  });
}
